import { Component, HostListener, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { DialogSeguimientoCotizacionComponent, DialogSeguimientoData } from '../../../../../common/dialogs/dialog-seguimiento-cotizacion/dialog-seguimiento-cotizacion.component';
import { EmailCotizacionService, EmailCotizacionRequest } from '../../../../../../services/email-cotizacion/email-cotizacion.service';
import { SnackBarService } from '../../../../../../utils/util.SnackBarService';
import {
  NetworkingQuotation,
  BrochureProgramaResponse
} from '../../../../../../services/servicioCotizador/cotizador.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';


export interface ModalEnviarCotizacionData {
  titulo: string;
  pdfUrl: string;
  pdfDownloadUrl?: string;
  idCotizacion?: number;
  numeroCotizacion?: string;
  nombreCliente?: string;
  fechaCreacion?: string;
  // Colectivo-only fields
  esColectivo?: boolean;
  emailDestinatario?: string;
  pdfBase64?: string;
  nombreContratante?: string;

  brochuresProgramas?: BrochureProgramaResponse[];
}

@Component({
  selector: 'app-modal-enviar-cotizacion',
  templateUrl: './modal-enviar-cotizacion.component.html',
  styleUrls: ['./modal-enviar-cotizacion.component.css']
})
export class ModalEnviarCotizacionComponent {
  pdfUrlSeguro: SafeResourceUrl;

  constructor(
    private dialogRef: MatDialogRef<ModalEnviarCotizacionComponent>,
    private dialog: MatDialog,
    private emailCotizacionService: EmailCotizacionService,
    private snackBarService: SnackBarService,
    private networkingQuotation: NetworkingQuotation,
    private sanitizer: DomSanitizer,
    @Inject(MAT_DIALOG_DATA) public data: ModalEnviarCotizacionData
  ) {
    this.pdfUrlSeguro = this.sanitizer.bypassSecurityTrustResourceUrl(data.pdfUrl);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.cerrar();
  }

  descargar(): void {
    const a = document.createElement('a');
    a.href = this.data.pdfDownloadUrl ?? this.data.pdfUrl;
    a.download = `cotización_${this.data.idCotizacion ?? ''}.pdf`;
    a.click();
  }

  cerrar(): void {
    this.dialogRef.close(false);
   
    if (this.data.esColectivo) {
      this.abrirSeguimiento();
    }
  }

  enviar(): void {
    this.dialogRef.close(false);

    if (this.data.esColectivo) {
      this.enviarCorreoColectivo();
    } else if (this.data.esColectivo === false) {
      this.enviarCorreoIndividual();
    } else {
      this.abrirSeguimiento();
    }
  }

  private construirAdjuntos(): {
    fileName: string;
    fileContent: string;
    mimeType: string;
  }[] {
    const attachments: {
      fileName: string;
      fileContent: string;
      mimeType: string;
    }[] = [];

    // Cotización principal
    if (this.data.pdfBase64?.trim()) {
      attachments.push({
        fileName: `cotizacion_${this.data.idCotizacion ?? ''}`,
        fileContent: this.limpiarBase64(this.data.pdfBase64),
        mimeType: '.pdf'
      });
    }

    // Brochures
    (this.data.brochuresProgramas ?? []).forEach((brochure) => {
      if (!brochure.pdfBase64?.trim()) {
        return;
      }

      const nombrePrograma = this.normalizarNombreArchivo(
        brochure.programa
      );

      attachments.push({
        fileName: `brochure_${nombrePrograma}`,
        fileContent: this.limpiarBase64(brochure.pdfBase64),
        mimeType: '.pdf'
      });
    });

    console.log('Adjuntos que serán enviados:', attachments.map((archivo) => ({
      fileName: archivo.fileName,
      mimeType: archivo.mimeType,
      base64Length: archivo.fileContent.length,
      inicioBase64: archivo.fileContent.substring(0, 10)
    })));

    return attachments;
  }

  private limpiarBase64(base64: string): string {
    const contenido = base64.trim();

    return contenido.includes(',')
      ? contenido.substring(contenido.indexOf(',') + 1)
      : contenido;
  }

  private normalizarNombreArchivo(nombre: string): string {
    return (nombre || 'programa')
      .trim()
      .replace(/[^a-zA-Z0-9_-]/g, '_');
  }
  

  private enviarCorreoColectivo(): void {
    const request: EmailCotizacionRequest = {
      to: this.data.emailDestinatario ?? '',
      cc: '',
      jsonParametros: JSON.stringify({
        '${nombre_contratante}': this.data.nombreContratante ?? '',
        '${id_cotizacion}': (this.data.idCotizacion ?? 0).toString()
      }),
      attachments: this.construirAdjuntos()
    };

     // Logs temporales para revisar los datos antes de enviar el correo
    console.log(
      'Programas recibidos en el modal:',
      this.data.brochuresProgramas
    );

    console.log('¿Llegó la cotización en Base64?', {
      existe: !!this.data.pdfBase64,
      longitud: this.data.pdfBase64?.length ?? 0,
      inicio: this.data.pdfBase64?.substring(0, 10)
    });

    console.log('Request de correo:', request);

    // Fire-and-forget: el seguimiento se abre independientemente del resultado del correo
    this.emailCotizacionService.enviarEmail(request).subscribe({
      next: () => {
        this.snackBarService.alert('btn-success', `Correo enviado correctamente a ${this.data.emailDestinatario}`);
        if (this.data.idCotizacion) {
          this.networkingQuotation.actualizarEstadoCotizacion(this.data.idCotizacion, 3).subscribe({
            error: (err) => console.error('Error al actualizar estado de cotización:', err)
          });
        }
      },
      error: () => this.snackBarService.alert('btn-danger', 'No se pudo enviar el correo. Verifique el email del destinatario.')
    });

    this.abrirSeguimiento();
  }

  private enviarCorreoIndividual(): void {
    const request: EmailCotizacionRequest = {
      to: this.data.emailDestinatario ?? '',
      cc: '',
      jsonParametros: JSON.stringify({
        '${nombre_contratante}': this.data.nombreContratante ?? this.data.nombreCliente ?? '',
        '${id_cotizacion}': (this.data.idCotizacion ?? 0).toString()
      }),
      attachments: this.construirAdjuntos()
    };

    this.emailCotizacionService.enviarEmail(request).subscribe({
      next: () => {
        this.snackBarService.alert('btn-success', `Correo enviado correctamente a ${this.data.emailDestinatario}`);
        if (this.data.idCotizacion) {
          this.networkingQuotation.actualizarEstadoCotizacion(this.data.idCotizacion, 3).subscribe({
            error: (err) => console.error('Error al actualizar estado de cotización:', err)
          });
        }
      },
      error: () => {
        this.snackBarService.alert('btn-danger', 'No se pudo enviar el correo. Verifique el email del destinatario.');
      }
    });

    this.abrirSeguimiento();
  }

  private abrirSeguimiento(): void {
    const dialogRef = this.dialog.open(DialogSeguimientoCotizacionComponent, {
      width: '900px',
      maxWidth: '95vw',
      data: {
        idCotizacion: this.data.idCotizacion || 0,
        numeroCotizacion: this.data.numeroCotizacion || '',
        nombreCliente: this.data.nombreCliente || '',
        fechaCreacion: this.data.fechaCreacion || '',
        probabilidadActual: '',
        canalesActuales: [],
        esColectivo: this.data.esColectivo ?? false
      } as DialogSeguimientoData
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result?.success) {
        console.log('Seguimiento guardado exitosamente');
      }
    });
  }
}

